import { useEventListener, useTimeoutFn } from '@vueuse/core';
import sortBy from 'lodash/sortBy';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { useLocalStorage } from '@/libraries/composables/useLocalStorage';

/**
 * @example
 * // Preregister before loading the script so isWidgetsReady waits for them
 * window.dispatchEvent(new CustomEvent('widget.preregister', { detail: 'miniGame' }));
 *
 * const { load } = useScriptTag('widget.js', undefined, { manual: true });
 * load().catch(() => {
 *     // Cancel preregistration on load failure so isWidgetsReady is not stuck
 *     window.dispatchEvent(new CustomEvent('widget.unregister', { detail: 'miniGame' }));
 * });
 *
 * // Inside widget.js — register once the widget is ready
 * try {
 *     // ... widget initialization logic
 *     window.dispatchEvent(new CustomEvent('widget.register', { detail: { positionId: 'miniGame', icon: 'https://example.com/icon.png' } }));
 * } catch {
 *     window.dispatchEvent(new CustomEvent('widget.unregister', { detail: 'miniGame' }));
 * }
 *
 * window.addEventListener('widget.open', (event) => {
 *     setTimeout(() => {
 *         window.dispatchEvent(new CustomEvent('widget.close'));
 *     }, 3000);
 * });
 */

declare global {
    interface WindowEventMap {
        'widget.preregister': CustomEvent<WidgetPositionId>;
        'widget.register': CustomEvent<IWidget>;
        'widget.unregister': CustomEvent<WidgetPositionId>;
        'widget.open': CustomEvent<WidgetPositionId>;
        'widget.close': CustomEvent<WidgetPositionId>;
    }
}

type WidgetPositionId = 'slots' | 'casino' | 'footballStrike' | 'miniGames';

interface IWidget {
    positionId: WidgetPositionId;
    icon: string;
}

const positionOrder: WidgetPositionId[] = ['slots', 'casino', 'footballStrike', 'miniGames'];

export const useWidgetStore = defineStore('widget', () => {
    const registeredWidgets = ref<Map<WidgetPositionId, IWidget>>(new Map());
    const activeWidget = ref<IWidget | null>(null);
    const isExpanded = ref(false);
    const [lastOpenedWidgetId, setLastOpenedWidgetId] = useLocalStorage<WidgetPositionId>('lastOpenedWidgetId', positionOrder[0]);

    const widgets = computed(() => sortBy(Array.from(registeredWidgets.value.values()), (widget: IWidget) => {
        const index = positionOrder.indexOf(widget.positionId);
        // unknown position id will be sorted to the end
        return index === -1 ? Infinity : index;
    }));

    const defaultWidget = computed(() => {
        if (lastOpenedWidgetId.value) {
            const lastOpenedWidget = registeredWidgets.value.get(lastOpenedWidgetId.value);
            if (lastOpenedWidget) return lastOpenedWidget;
        }
        return widgets.value[0];
    });

    const pendingRegistrations = shallowRef<Set<WidgetPositionId>>(new Set());
    const isWidgetsReady = computed(() => pendingRegistrations.value.size === 0);

    function addPendingRegistration(positionId: WidgetPositionId) {
        const nextPendingRegistrations = new Set(pendingRegistrations.value);
        nextPendingRegistrations.add(positionId);
        pendingRegistrations.value = nextPendingRegistrations;
    }

    function removePendingRegistration(positionId: WidgetPositionId): boolean {
        const nextPendingRegistrations = new Set(pendingRegistrations.value);
        const isRemoved = nextPendingRegistrations.delete(positionId);
        if (isRemoved) pendingRegistrations.value = nextPendingRegistrations;
        return isRemoved;
    }

    /** Preregister a widget so that isWidgetsReady will wait for it to register */
    function preregisterWidget(positionId: WidgetPositionId) {
        addPendingRegistration(positionId);
        useTimeoutFn(() => {
            const isRemoved = removePendingRegistration(positionId);
            if (isRemoved) console.warn('Registration timeout for widget position', positionId);
        }, __GIVE_UP_WAITING_WIDGET_RESPONSE_TIME__);
    }

    function registerWidget(widget: IWidget) {
        registeredWidgets.value.set(widget.positionId, widget);
        removePendingRegistration(widget.positionId);
    }

    function unregisterWidget(positionId: WidgetPositionId) {
        removePendingRegistration(positionId);
        registeredWidgets.value.delete(positionId);
        if (activeWidget.value?.positionId === positionId) {
            window.dispatchEvent(new CustomEvent('widget.close'));
        }
    }

    function openWidget(positionId: WidgetPositionId) {
        const widget = registeredWidgets.value.get(positionId);
        if (widget) {
            activeWidget.value = widget;
            setLastOpenedWidgetId(positionId);
            window.dispatchEvent(new CustomEvent<WidgetPositionId>('widget.open', { detail: positionId }));
        }
    }

    function closeWidget(_positionId: WidgetPositionId) {
        activeWidget.value = null;
        isExpanded.value = false;
    }

    useEventListener('widget.preregister', event => preregisterWidget(event.detail));
    useEventListener('widget.register', event => registerWidget(event.detail));
    useEventListener('widget.unregister', event => unregisterWidget(event.detail));
    useEventListener('widget.close', event => closeWidget(event.detail));

    return {
        widgets,
        activeWidget,
        defaultWidget,
        isWidgetsReady,
        isExpanded,
        preregisterWidget,
        unregisterWidget,
        openWidget,
        closeWidget,
    };
});
